// Fiche d'un module sur l'écran du super administrateur (D-080) : en-tête avec l'état de livraison et
// l'activation globale, puis trois onglets : ce que fait le module, comment l'utiliser (étapes de
// démonstration, comptes, écrans), réglage par collectivité.
import type { EtatLivraison, ModulePlateforme, ModulesPlateforme } from '@cc/contrats';
import { LIBELLES_ETAT_LIVRAISON } from '@cc/contrats';
import { Bouton, Lien, Onglets, PuceEtat } from '@cc/ui';
import { useState } from 'react';
import { date } from '../../commun/format';
import styles from './plateforme.module.css';

export interface CibleModification {
  module: ModulePlateforme;
  actif: boolean;
  collectivite?: { id: string; nom: string };
}

interface ProprietesFiche {
  module: ModulePlateforme;
  collectivites: ModulesPlateforme['collectivites'];
  onModifier: (cible: CibleModification) => void;
}

const TON_LIVRAISON: Record<EtatLivraison, 'ok' | 'attente' | 'neutre'> = { livre: 'ok', partiel: 'attente', prevu: 'neutre' };

export function FicheModulePlateforme({ module: m, collectivites, onModifier }: ProprietesFiche) {
  const [ouvert, setOuvert] = useState(false);
  const f = m.fiche;
  const idTitre = `module-${m.module}`;
  const idDetail = `detail-${m.module}`;
  return (
    <article className={styles.fiche} aria-labelledby={idTitre}>
      <header className={styles.entete}>
        <span className={styles.code}>{m.module}</span>
        <div className={styles.titre}>
          <h2 id={idTitre}>{m.libelle}</h2>
          <p className={styles.resume}>{f.resume}</p>
          <div className={styles.pastilles}>
            <PuceEtat ton={TON_LIVRAISON[f.etat]} libelle={`${LIBELLES_ETAT_LIVRAISON[f.etat]}, lot ${f.lot}`} />
            <PuceEtat ton={m.actif_plateforme ? 'ok' : 'neutre'} libelle={m.actif_plateforme ? 'Actif sur la plateforme' : 'Désactivé sur la plateforme'} />
            {m.motif ? <small className={styles.motif}>Motif : {m.motif}</small> : null}
          </div>
        </div>
        <div className={styles.actions}>
          <Bouton
            petit
            variante={m.actif_plateforme ? 'secondaire' : 'principal'}
            onClick={() => onModifier({ module: m, actif: !m.actif_plateforme })}
            aria-label={`${m.actif_plateforme ? 'Désactiver' : 'Activer'} ${m.module} sur la plateforme`}
          >
            {m.actif_plateforme ? 'Désactiver' : 'Activer'}
          </Bouton>
          <Bouton petit variante="discret" onClick={() => setOuvert((o) => !o)} aria-expanded={ouvert} aria-controls={idDetail}>
            {ouvert ? 'Replier' : 'Détails'}
          </Bouton>
        </div>
      </header>
      {ouvert ? (
        <div id={idDetail} className={styles.detail}>
          <Onglets
            libelle={`Détails du module ${m.module}`}
            onglets={[
              {
                id: 'description',
                libelle: 'Ce que fait le module',
                contenu: (
                  <ul className={styles.points}>
                    {f.description.map((d) => (
                      <li key={d}>{d}</li>
                    ))}
                  </ul>
                ),
              },
              {
                id: 'utilisation',
                libelle: 'Comment l’utiliser',
                contenu: (
                  <>
                    <ol className={styles.etapes}>
                      {f.utilisation.map((u) => (
                        <li key={u}>{u}</li>
                      ))}
                    </ol>
                    {f.comptes.length > 0 ? (
                      <p className={styles.meta}>
                        Comptes de présentation :{' '}
                        {f.comptes.map((c, i) => (
                          <span key={c}>
                            {i > 0 ? ', ' : ''}
                            <code>{c}</code>
                          </span>
                        ))}
                        . Les mots de passe sont affichés sur la page de connexion.
                      </p>
                    ) : null}
                    {f.ecrans.length > 0 ? (
                      <p className={styles.meta}>
                        Écrans :{' '}
                        {f.ecrans.map((e, i) => (
                          <span key={e}>
                            {i > 0 ? ', ' : ''}
                            <Lien href={e}>{e}</Lien>
                          </span>
                        ))}
                      </p>
                    ) : (
                      <p className={styles.meta}>Aucun écran disponible pour l&apos;instant.</p>
                    )}
                  </>
                ),
              },
              {
                id: 'collectivites',
                libelle: 'Collectivités',
                contenu: (
                  <div className={styles.tableau}>
                    {!m.actif_plateforme ? <p className={styles.meta}>Module désactivé sur la plateforme : les réglages locaux ci-dessous restent sans effet tant qu&apos;il n&apos;est pas réactivé.</p> : null}
                    <table>
                      <thead>
                        <tr>
                          <th scope="col">Collectivité</th>
                          <th scope="col">État local</th>
                          <th scope="col">Motif</th>
                          <th scope="col">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {collectivites.map((c) => {
                          const etat = m.collectivites.find((x) => x.collectivite_id === c.id);
                          const actif = etat?.actif ?? false;
                          return (
                            <tr key={c.id}>
                              <th scope="row">
                                {c.nom} <small>({c.code_court})</small>
                              </th>
                              <td>
                                <PuceEtat ton={actif ? 'ok' : 'neutre'} libelle={actif ? 'Actif' : 'Non activé'} />
                                {etat?.modifie_le ? <small> le {date(etat.modifie_le)}</small> : null}
                              </td>
                              <td>{etat?.motif ?? ''}</td>
                              <td>
                                <Bouton
                                  petit
                                  variante={actif ? 'secondaire' : 'principal'}
                                  onClick={() => onModifier({ module: m, actif: !actif, collectivite: { id: c.id, nom: c.nom } })}
                                  aria-label={`${actif ? 'Désactiver' : 'Activer'} ${m.module} pour ${c.nom}`}
                                >
                                  {actif ? 'Désactiver' : 'Activer'}
                                </Bouton>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ),
              },
            ]}
          />
        </div>
      ) : null}
    </article>
  );
}
